import React, { useState, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMyPokeTractor } from '../../../../interfaces/OwnTractor.interfaces';

interface TractorCardProps extends IMyPokeTractor {
  isLast?: boolean;
  onCancel: (id: string) => void;
}

const TractorCard: React.FC<TractorCardProps> = ({
  id,
  brand,
  machine,
  manufacturedYear,
  operationTime,
  region,
  views,
  pokeCount,
  image,
  isLast = false,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('button')) {
      navigate(`/tractor-poking/${id}`);
    }
  };

  const handleCardKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const target = e.target as HTMLElement;
      if (!target.closest('button')) {
        navigate(`/tractor-poking/${id}`);
      }
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancel(id);
  };

  const handleCancelKeyPress = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCancel(id);
    }
  };

  const formatYear = (year: number) => {
    if (!year) return '';
    return year.toString().slice(-2);
  };

  const renderImageOrSkeleton = () => {
    if (imageError) {
      return <div className={'absolute inset-0 bg-[#e0e0e0] animate-pulse rounded-[8px]'} />;
    }

    return (
      <>
        {isLoading && <div className={'absolute inset-0 bg-[#e0e0e0] animate-pulse rounded-[8px]'} />}
        <img
          src={
            image ||
            brand.image ||
            'https://images.pexels.com/photos/789206/pexels-photo-789206.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
          }
          alt={`${brand.name} ${machine.model}`}
          onError={handleImageError}
          onLoad={handleImageLoad}
          className={`absolute inset-0 w-full h-full object-cover rounded-[8px] ${isLoading ? 'invisible' : 'visible'}`}
        />
      </>
    );
  };

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={handleCardKeyPress}
      className={`pt-6 ${isLast ? 'pb-0' : 'pb-6'} cursor-pointer bg-white ${
        !isLast ? 'border-b border-[#ECECF3]' : ''
      }`}
      role={'button'}
      tabIndex={0}
      aria-label={`${brand.name} ${machine.model} 트랙터 상세 정보`}
    >
      <div className={'flex gap-[16px]'}>
        <div className={'relative flex-shrink-0 w-[150px]'}>{renderImageOrSkeleton()}</div>
        <div className={'flex-1 flex flex-col gap-4 py-1'}>
          <div className={'flex flex-col gap-2'}>
            <h3 className={'text-[16px] leading-[130%] font-normal text-[#232429]'}>
              {brand.name} {machine.model}
              {' 트랙터 ('}
              {machine.engineOutputInPs}
              {'마력)'}
            </h3>
            <div className={'inline-flex gap-[2px] px-[6px] py-[4px] items-center bg-[#F2F2F7] rounded-[4px] w-fit'}>
              <svg width={'14'} height={'14'} viewBox={'0 0 14 14'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
                <path
                  d={
                    'M6.9998 1.75H6.9991C4.68 1.7514 2.7998 3.6862 2.7998 6.0732C2.7998 8.4602 5.198 11.0124 6.9977 12.2479V12.25L6.9984 12.2493L6.9991 12.25V12.2486C8.7988 11.0131 11.197 8.4602 11.197 6.0739C11.1977 3.6862 9.31821 1.7514 6.9998 1.75ZM6.9998 8.05C5.83991 8.05 4.8998 7.1099 4.8998 5.95C4.8998 4.7901 5.83991 3.85 6.9998 3.85C8.15971 3.85 9.0998 4.7901 9.0998 5.95C9.0998 7.1099 8.15971 8.05 6.9998 8.05Z'
                  }
                  fill={'#FF3049'}
                />
              </svg>
              <span className={'text-[#232429] text-[14px] font-normal leading-[85%]'}>{region}</span>
            </div>
            <p className={'text-[14px] leading-[130%] font-normal text-[#232429]'}>
              {formatYear(manufacturedYear)}
              {'년식 · '}
              {operationTime}
              {'시간'}
            </p>
          </div>
          <div className={'w-full h-[1px] bg-[#ECECF3]'} />
          <div className={'flex flex-col gap-1'}>
            <p className={'text-[14px] leading-[130%] font-normal text-[#232429]'}>
              <strong>
                {pokeCount}
                {'명'}
              </strong>{' '}
              {'신청중'}
            </p>
            <p className={'text-[14px] leading-[130%] font-normal text-[#232429]'}>
              <strong>
                {views}
                {'명'}
              </strong>{' '}
              {'구경중'}
            </p>
          </div>
          <div className={'flex justify-end'}>
            <button
              onClick={handleCancelClick}
              onKeyDown={handleCancelKeyPress}
              className={
                'h-[32px] max-h-[32px] px-2 py-[6px] flex items-center gap-1 rounded-[80px] border border-[#ECECF3] text-[14px] font-medium leading-[85%] text-[#505363] bg-white'
              }
              aria-label={`${brand.name} ${machine.model} 트랙터 찔러보기 취소`}
            >
              <svg width={'24'} height={'24'} viewBox={'0 0 24 24'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
                <path
                  d={
                    'M10.9534 2.45549C6.59868 2.91629 3.01788 6.4263 2.47668 10.7715C2.20548 12.9483 2.66748 14.9968 3.64428 16.7259C3.82428 17.0439 4.26108 17.1075 4.54428 16.8747L6.19668 15.5115C6.60948 15.1707 6.75948 14.6079 6.57348 14.1051C5.83188 12.1059  6.26148 9.77069 7.86828 8.16269C8.77548 7.25549 10.5923 6.04589 12.5687 5.09069C12.9431 4.90949 13.3931 4.99349 13.6871 5.28869C14.0735 5.67509 14.0735 6.30269 13.6871 6.68909L11.3099 9.06869C11.0099 9.36869 11.0099 9.85589 11.3099 10.1559C11.6099 10.4559 12.0971 10.4559 12.3971 10.1559L14.7515 7.80029C15.1919 7.35989 15.9323 7.40429 16.3127 7.93229C16.6115 8.34629 16.5227 8.92709 16.1627 9.28829L13.8467 11.6043C13.5467 11.9043 13.5467 12.3915 13.8467 12.6915C14.1467 12.9915 14.6327 12.9915 14.9339 12.6915L17.3123 10.3131C17.6987 9.92669 18.3263 9.92669 18.7127 10.3131C19.0067 10.6071 19.0919 11.0571 18.9107 11.4315C17.9555 13.4079 16.7459 15.2247 15.8387 16.1319C14.2307 17.7399 11.8955 18.1695 9.89508 17.4267C9.39228 17.2407 8.82948 17.3907 8.48868 17.8035L7.12548 19.4559C6.89268 19.7379 6.95628 20.1747 7.27428 20.3559C8.99748 21.3339 11.0519 21.7947 13.2287 21.5235C17.5739 20.9823 21.0827 17.4027 21.5447 13.0479C22.1927 6.92429 17.0771 1.80749 10.9534 2.45549Z'
                  }
                  fill={'#34C77E'}
                />
              </svg>
              {'찔러보기 취소'}
              <svg width={'14'} height={'14'} viewBox={'0 0 14 14'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
                <path
                  d={
                    'M4.27047 12.6699C4.06047 12.6699 3.85047 12.5999 3.71047 12.4599C3.43047 12.1799 3.50047 11.7599 3.78047 11.4799L8.75047 7.13989L3.78047 2.65989C3.50047 2.37989 3.43047 1.95989 3.71047 1.67989C3.99047 1.39989 4.41047 1.32989 4.69047 1.60989L10.2905 6.50989C10.4305 6.64989 10.5005 6.85989 10.5005 7.06989C10.5005 7.27989 10.4305 7.48989 10.2905 7.62989L4.69047 12.5299C4.55047 12.5999 4.41047 12.6699 4.27047 12.6699Z'
                  }
                  fill={'#505363'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TractorCard;
